import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import DSDialog from "../design-system/DSDialog";
import Icon from "../Icon";
import designTokens, {
  colors,
  numbers,
  typography,
} from "../../styles/designTokens";

interface ConversionSupportModalProps {
  open: boolean;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  onClose: () => void;
  onSend: () => void;
  sending?: boolean;
  error?: string | null;
}

const formatBytes = (bytes: number | null | undefined): string | null => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const format = (value: number, maximumFractionDigits: number) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(value);
  if (bytes < 1024 * 1024) return `${format(bytes / 1024, 1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${format(bytes / (1024 * 1024), 1)} MB`;
  }
  return `${format(bytes / (1024 * 1024 * 1024), 2)} GB`;
};

const supportButtonSx = {
  height: 48,
  minWidth: 100,
  px: numbers.gap[12],
  borderRadius: numbers.cornerRadius[8],
  ...typography.styles.viewrStudio.bodyRegular,
};

const ConversionSupportModal: React.FC<ConversionSupportModalProps> = ({
  open,
  fileName,
  fileSizeBytes,
  onClose,
  onSend,
  sending = false,
  error,
}) => (
  <DSDialog
    open={open}
    onClose={sending ? undefined : onClose}
    disableEscapeKeyDown={sending}
    size="md"
    aria-labelledby="conversion-support-title"
    aria-describedby="conversion-support-description"
    paperSx={{ width: designTokens.semantic.conversionReport.width }}
  >
    <Box aria-hidden="true" sx={{ height: numbers.gap[16] }} />
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: numbers.gap[64],
        px: numbers.gap[32],
        gap: numbers.gap[8],
      }}
    >
      <Typography
        id="conversion-support-title"
        sx={{
          ...typography.styles.viewrStudio.headingMedium,
          color: colors.grey[90].dark,
          flex: 1,
          minWidth: 0,
        }}
      >
        Send report to support
      </Typography>
      <IconButton
        aria-label="Close support report"
        onClick={onClose}
        disabled={sending}
        sx={{
          width: 24,
          height: 24,
          p: 0,
          color: colors.grey[60].dark,
          "&:hover": { bgcolor: "transparent" },
        }}
      >
        <Icon name="close-24" aria-hidden="true" />
      </IconButton>
    </Box>

    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: numbers.gap[16],
        px: numbers.gap[32],
        pt: numbers.gap[4],
        pb: numbers.gap[12],
      }}
    >
      <Typography
        id="conversion-support-description"
        sx={{
          ...typography.styles.viewrStudio.headline1Regular,
          color: colors.grey[90].dark,
        }}
      >
        Sending this report helps us find why the conversion failed.
        <br />
        We'll reply to your currently signed-in email.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography
          sx={{
            ...typography.styles.viewrStudio.label2Regular,
            color: colors.grey[60].dark,
          }}
        >
          Always included
        </Typography>
        <Box
          component="ul"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            m: 0,
            p: 0,
            listStyle: "none",
          }}
        >
          {[
            ["Conversion log", "Error code and where it stopped"],
            [
              "Object info",
              [
                fileName || "Object",
                fileName?.split(".").slice(1).pop()?.toUpperCase(),
                formatBytes(fileSizeBytes),
              ].filter(Boolean).join(" - "),
            ],
          ].map(([label, description]) => (
            <Box
              component="li"
              key={label}
              sx={{ display: "flex", alignItems: "baseline", gap: numbers.gap[12] }}
            >
              <Box
                aria-hidden="true"
                sx={{ width: 6, height: 6, flexShrink: 0, borderRadius: "50%", bgcolor: colors.grey[60].dark, alignSelf: "center" }}
              />
              <Typography
                sx={{ ...typography.styles.viewrStudio.label2Regular, color: colors.grey[90].dark, flex: 1, flexShrink: 0 }}
              >
                {label}
              </Typography>
              <Typography
                sx={{ ...typography.styles.viewrStudio.label2Regular, color: colors.grey[60].dark, minWidth: 0, overflowWrap: "anywhere", textAlign: "right" }}
              >
                {description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {error && (
        <Typography
          role="alert"
          sx={{ ...typography.styles.viewrStudio.captionRegular, color: colors.red[50].dark }}
        >
          {error}
        </Typography>
      )}
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        minHeight: numbers.gap[64],
        px: numbers.gap[32],
        gap: numbers.gap[24],
      }}
    >
      <Button
        variant="outlined"
        onClick={onClose}
        disabled={sending}
        sx={{ ...supportButtonSx, color: colors.grey[80].dark, borderColor: colors.grey[60].dark }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onSend}
        disabled={sending}
        sx={{
          ...supportButtonSx,
          bgcolor: designTokens.semantic.button.primaryAction.background.default.dark,
          color: colors.common[100].dark,
          boxShadow: "none",
          "&:hover": { bgcolor: designTokens.semantic.button.primaryAction.background.hover.dark, boxShadow: "none" },
          "&.Mui-disabled": { bgcolor: colors.grey[40].dark, color: colors.grey[80].dark },
        }}
      >
        {sending ? "Sending…" : "Send report"}
      </Button>
    </Box>
    <Box aria-hidden="true" sx={{ height: numbers.gap[16] }} />
  </DSDialog>
);

export default ConversionSupportModal;
